import React, { useState, useEffect } from 'react';
import { FaUser, FaSignOutAlt, FaBars } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { sanitizeUrl } from '../../utils/url';

import ProfileModal from '../Modals/ProfileModal';

import NotificationBell from './NotificationBell';
import api from '../../api';
import { getEcho, disconnectEcho } from '../../echo';
import { ReactComponent as CloudIcon } from '../../assets/Cloud.svg';
import { useI18n } from '../../i18n';
import { useDashboard } from '../../context/DashboardContext';
import { resolveNotificationNav } from '../../constants/notificationNavMap';

const Header = () => {
    const { locale, setLocale, t } = useI18n();
    const navigate = useNavigate();
    const { setActiveView, setDeepLink, setIsNotificationsOpen, toggleMobileSidebar } = useDashboard();
    const [showDropdown, setShowDropdown] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
    const [latestNotification, setLatestNotification] = useState(null);
    const [notificationLoading, setNotificationLoading] = useState(true);
    const [weather, setWeather] = useState({ condition: t('common.weather.cloudy'), icon: 'cloudy' });
    const [weatherLoading, setWeatherLoading] = useState(true);
    const supportedLocales = ['en', 'ur', 'ar', 'fa'];
    const activeLocale = supportedLocales.includes(locale) ? locale : 'en';

    // Fetch weather on mount
    useEffect(() => {
        const fetchWeather = async () => {
            try {
                const response = await api.get('/weather');
                if (response?.status && response?.data) {
                    setWeather(response.data);
                }
            } catch (error) {
                console.debug('Weather fetch failed:', error.message);
            } finally {
                setWeatherLoading(false);
            }
        };

        fetchWeather();
    }, [locale]);

    // Fetch the latest notification on mount
    useEffect(() => {
        const fetchLatestNotification = async () => {
            try {
                const response = await api.get('/notifications?limit=1');
                if (response.status && response.data.data.length > 0) {
                    setLatestNotification(response.data.data[0]);
                }
            } catch (error) {
                console.error('Error fetching latest notification:', error);
            } finally {
                setNotificationLoading(false);
            }
        };

        fetchLatestNotification();

        const userStr = localStorage.getItem('user');
        if (!userStr) return;

        const user = JSON.parse(userStr);
        const userId = user.id;

        if (userId) {
            const echo = getEcho();
            if (echo) {
                const channel = echo.private(`App.Models.User.${userId}`);
                channel.notification((notification) => {
                    setLatestNotification({
                        id: notification.id,
                        data: {
                            link: notification.link,
                            act: notification.act,
                            msg: notification.msg,
                            actor_name: notification.actor_name,
                            target_id: notification.target_id,
                            target_type: notification.target_type,
                            target_meta: notification.target_meta,
                        },
                        created_at: notification.created_at,
                        read_at: notification.read_at,
                    });
                });
            }
        }
    }, []);

    const handleLogout = async () => {
        try {
            await api.post('/logout');
        } catch (_) {
        } finally {
            disconnectEcho();
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
    };

    const getInitials = (name) => {
        if (!name) return t('common.misc.user').charAt(0).toUpperCase();
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const handleProfileUpdate = (updatedUser) => {
        setCurrentUser(updatedUser);
    };

    const getNotificationMessage = () => {
        if (!latestNotification) {
            return t('common.notifications.none');
        }

        const actor = latestNotification.data?.actor_name;
        const msg = latestNotification.data?.msg || latestNotification.data?.act || t('common.notifications.new');
        const full = actor ? `${actor}: ${msg}` : msg;
        return full.length > 60 ? full.substring(0, 57) + '...' : full;
    };

    const handleNotificationClick = () => {
        if (!latestNotification) {
            setIsNotificationsOpen(true);
            return;
        }

        const nav = resolveNotificationNav(latestNotification);

        if (!nav || !nav.view) {
            setIsNotificationsOpen(true);
            return;
        }

        // Mark as read optimistically
        if (!latestNotification.read_at) {
            setLatestNotification((prev) => ({ ...prev, read_at: new Date().toISOString() }));
            api.post(`/notifications/${latestNotification.id}/read`).catch(() => {});
        }

        setActiveView(nav.view);
        if (nav.deepLink) {
            setDeepLink(nav.deepLink);
        }
    };

    return (
        <div className="header">
            <button
                type="button"
                className="header-menu-toggle"
                onClick={toggleMobileSidebar}
                aria-label={t('navigation.toggle_menu')}
            >
                <FaBars />
            </button>

            {notificationLoading ? (
                <div className="header-notification-box header-skeleton">
                    <div className="skeleton-notification-text"></div>
                </div>
            ) : (
                <div
                    className={`header-notification-box${latestNotification ? ' header-notification-box--clickable' : ''}${latestNotification && !latestNotification.read_at ? ' header-notification-box--unread' : ''}`}
                    onClick={handleNotificationClick}
                    role={latestNotification ? 'button' : undefined}
                    tabIndex={latestNotification ? 0 : undefined}
                    onKeyDown={latestNotification ? (e) => e.key === 'Enter' && handleNotificationClick() : undefined}
                >
                    <span className="notification-box-text">
                        {getNotificationMessage()}
                    </span>
                </div>
            )}

            {weatherLoading ? (
                <div className="header-weather header-skeleton">
                    <div className="skeleton-weather-icon"></div>
                    <div className="skeleton-weather-text"></div>
                </div>
            ) : (
                <div className="header-weather">
                    <CloudIcon className="weather-icon" />
                    <span>{weather.condition}</span>
                </div>
            )}

            <div className="header-actions">
                <div className="header-language-wrapper">
                    <label htmlFor="header-language-select" className="visually-hidden">
                        {t('common.language.label')}
                    </label>
                    <select
                        id="header-language-select"
                        className="header-language-select"
                        value={activeLocale}
                        onChange={(event) => setLocale(event.target.value)}
                        aria-label={t('common.language.label')}
                    >
                        <option value="en">{t('common.languages.english')}</option>
                        <option value="ur">{t('common.languages.urdu')}</option>
                        <option value="ar">{t('common.languages.arabic')}</option>
                        <option value="fa">{t('common.languages.farsi')}</option>
                    </select>
                </div>

                <NotificationBell />

                <div className="user-profile-wrapper">
                    <div className="user-profile" onClick={() => setShowDropdown(!showDropdown)}>
                        <span className="user-name">{currentUser.name || t('common.misc.user')}</span>
                        <div className="user-avatar">
                            {currentUser.avatar ? (
                                <img
                                    src={sanitizeUrl(
                                        currentUser.avatar.startsWith('http')
                                            ? currentUser.avatar
                                            : (currentUser.avatar.startsWith('/storage')
                                                ? `http://localhost:8648${currentUser.avatar}`
                                                : `http://localhost:8648/storage/${currentUser.avatar}`)
                                    )}
                                    alt={t('common.misc.avatar')}
                                />
                            ) : (
                                <div className="avatar-initials">{getInitials(currentUser.name)}</div>
                            )}
                        </div>
                    </div>

                    {showDropdown && (
                        <div className="profile-dropdown">
                            <div className="dropdown-header">
                                <div className="dropdown-user-info">
                                    <div className="dropdown-user-name">{currentUser.name}</div>
                                    <div className="dropdown-user-email">{currentUser.email}</div>
                                </div>
                            </div>
                            <div className="dropdown-divider"></div>
                            <div className="dropdown-item" onClick={() => { setShowDropdown(false); setIsProfileOpen(true); }}>
                                <FaUser className="dropdown-icon" />
                                <span>{t('navigation.profile')}</span>
                            </div>
                            <div className="dropdown-item logout" onClick={handleLogout}>
                                <FaSignOutAlt className="dropdown-icon" />
                                <span>{t('navigation.logout')}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ProfileModal
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={currentUser}
                onUpdate={handleProfileUpdate}
            />
        </div>
    );
};

export default Header;
